import React from 'react';
import { ReactComponent as CheckIcon } from '@repo/common/assets/icons/16px/icon_16px_check-circle_outline.svg';
import { JOB_LABEL, cn } from '../model/derive';
import {
  BEHAVIORS,
  SCALE,
  STRENGTHS,
  countMissing,
  missingOf,
  samePick,
} from '../model/completeProject';
import TdButton from '../Button';
import { Alert, Card, CardTitle, Footer, LeaderBadge } from './ui';
import { ReactComponent as TickIcon } from '@repo/common/assets/icons/16px/icon_16px_check.svg';

/** Complete project 챕터 화면 (팀원 평가 · 회고 · Kit) */

const DOT = {
  none: 'bg-grayscale-gray05',
  progress: 'bg-green-deep02',
  done: 'bg-functional-success03',
};

export const ChapterNav = ({ items, current, onGo }) => (
  <nav
    aria-label='Chapters'
    className='-mx-0.5 mb-2.5 flex gap-1.5 overflow-x-auto px-0.5 pb-1 pt-0.5'
  >
    {items.map((it) => {
      const on = it.key === current;
      const done = it.status === 'done' && !on;
      return (
        <button
          key={it.key}
          type='button'
          aria-current={on ? 'step' : undefined}
          onClick={() => onGo(it.key)}
          className={cn(
            'flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 text-[13px] font-medium',
            on
              ? 'bg-green-deep04 text-grayscale-gray00'
              : 'bg-grayscale-gray00 text-text-primary shadow-[inset_0_0_0_1px_rgba(0,158,79,0.2)]',
          )}
        >
          {done ? (
            <CheckIcon className='h-3.5 w-3.5 text-functional-success03' />
          ) : (
            <span
              aria-hidden
              className={cn(
                'h-1.5 w-1.5 rounded-full',
                on ? 'bg-grayscale-gray00' : DOT[it.status],
              )}
            />
          )}
          {it.label}
        </button>
      );
    })}
  </nav>
);

const Head = ({ nav, kicker, title, leader, sub }) => (
  <div className='flex flex-col gap-1.5'>
    {nav}
    <span className='text-[13px] leading-[140%] text-text-secondary'>{kicker}</span>
    <div className='flex flex-wrap items-center gap-2'>
      <h1 className='text-[24px] font-medium leading-[120%] tracking-[-0.01em] text-text-primary md:text-[28px]'>
        {title}
      </h1>
      {leader && <LeaderBadge large />}
    </div>
    <span className='text-[15px] leading-[150%] text-text-secondary [text-wrap:pretty]'>{sub}</span>
  </div>
);

const Page = ({ children }) => (
  <main className='flex flex-1 justify-center px-4 pt-5 md:px-10 md:pb-16 md:pt-10'>
    <div className='flex w-full max-w-[760px] flex-col gap-4 md:gap-6'>{children}</div>
  </main>
);

const ChapterFooter = ({ editable, savedText, saveError, errText, actions, onBackToHub }) =>
  editable ? (
    <Footer>
      <div className='flex flex-col gap-0.5'>
        <span
          className={cn(
            'text-[13px] leading-[140%] tabular-nums',
            saveError ? 'text-text-failed' : 'text-text-secondary',
          )}
        >
          {saveError ? 'Couldn’t save. Check your connection.' : savedText}
        </span>
        {errText && <span className='text-[13px] leading-[140%] text-text-failed'>{errText}</span>}
      </div>
      <div className='flex w-full gap-2 md:w-auto [&>*]:flex-1 md:[&>*]:flex-none'>{actions}</div>
    </Footer>
  ) : (
    <Footer>
      <span className='text-[13px] leading-[140%] text-text-secondary'>
        Read-only · nothing can be edited after submitting
      </span>
      <div className='flex w-full gap-2 md:w-auto [&>*]:flex-1 md:[&>*]:flex-none'>
        <TdButton variant='soft' size='l' onClick={onBackToHub}>
          Back to chapters
        </TdButton>
      </div>
    </Footer>
  );

const Choice = ({ on, disabled, onPick, className, children }) => (
  <button
    type='button'
    role='radio'
    aria-checked={on}
    disabled={disabled}
    onClick={onPick}
    className={cn(
      'min-w-0 whitespace-nowrap text-[13px] disabled:cursor-default',
      on
        ? 'bg-surface-green10 font-medium text-green-deep04 shadow-[inset_0_0_0_1.5px_#1dc37b]'
        : 'bg-grayscale-gray00 text-text-primary shadow-[inset_0_0_0_1px_rgba(0,158,79,0.2)]',
      className,
    )}
  >
    {children}
  </button>
);

const ErrorText = ({ children }) => (
  <span className='text-[13px] leading-[140%] text-text-failed'>{children}</span>
);

const TextField = ({ label, required, value, onChange, placeholder, error, readOnly }) => (
  <label className='flex flex-col gap-1.5'>
    <span className='text-[13px] font-medium leading-[140%] text-text-primary'>
      {label}
      {required ? (
        <span className='text-text-accent'>*</span>
      ) : (
        <span className='font-normal text-text-secondary'> · optional</span>
      )}
    </span>
    <textarea
      rows={3}
      value={value}
      readOnly={readOnly}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      aria-invalid={!!error}
      className={cn(
        'min-h-[88px] w-full resize-y rounded-xl border-0 bg-grayscale-gray00 px-3 py-2.5 text-[14px] leading-[150%] text-text-primary outline-none placeholder:text-text-placeholder focus:shadow-[inset_0_0_0_1.5px_#1dc37b]',
        error ? 'shadow-[inset_0_0_0_1px_#ff4550]' : 'shadow-[inset_0_0_0_1px_rgba(0,158,79,0.2)]',
      )}
    />
    {error && <ErrorText>Write a few words.</ErrorText>}
  </label>
);

export const MateChapter = ({
  nav,
  mate,
  index,
  total,
  answer,
  context,
  editable,
  showErrors,
  savedText,
  saveError,
  onChange,
  onSaveBack,
  onNext,
  nextLabel,
  onBackToHub,
}) => {
  const a = answer ?? {};
  const mm = missingOf(a);
  const n = countMissing(mm);
  const err = (flag) => showErrors && flag;
  const set = (patch) => onChange((prev) => ({ ...prev, ...patch(prev) }));
  const setScore = (key, v) => set((p) => ({ scores: { ...(p.scores ?? {}), [key]: v } }));
  return (
    <Page>
      <Head
        nav={nav}
        kicker={`Teammate ${index + 1} of ${total}`}
        title={mate.name}
        leader={mate.role === 'leader'}
        sub={`${JOB_LABEL[mate.job] ?? mate.job}${editable ? '' : ' · read-only'}`}
      />

      <div className='flex flex-col gap-3 rounded-xl bg-green-deep03/[0.04] px-4 py-3.5'>
        <span className='text-[12px] font-medium leading-[140%] text-text-secondary'>
          Worked together
        </span>
        {context.length ? (
          context.map((x, i) => (
            <div key={i} className='flex flex-col gap-0.5'>
              <span className='text-[14px] font-medium leading-[140%] text-text-primary'>
                {x.t}
              </span>
              <span className='text-[13px] leading-[140%] text-text-secondary'>{x.d}</span>
            </div>
          ))
        ) : (
          <span className='text-[13px] leading-[140%] text-text-secondary'>
            No handoffs or questions between you two on the board. Think of check-ins, reviews and
            team threads.
          </span>
        )}
      </div>

      <Card>
        <CardTitle>How they worked</CardTitle>
        <span className='-mt-2 text-[13px] leading-[150%] text-text-secondary'>
          Pick how often you saw this. If you can’t tell, choose “Didn’t see this”.
        </span>
        {BEHAVIORS.map((b, k) => {
          const v = a.scores?.[b.key];
          return (
            <div
              key={b.key}
              role='radiogroup'
              aria-label={b.label}
              className='flex flex-col gap-2.5 border-t border-green-deep03/[0.12] pt-4'
            >
              <div className='flex gap-2'>
                <span className='text-[14px] font-medium leading-[140%] text-text-secondary tabular-nums'>
                  {k + 1}
                </span>
                <span className='text-[14px] font-medium leading-[140%] text-text-primary'>
                  {b.label}
                </span>
              </div>
              <div className='flex flex-col gap-1.5 md:flex-row'>
                {SCALE.map((label, i) => (
                  <Choice
                    key={label}
                    on={v === i}
                    disabled={!editable}
                    onPick={() => setScore(b.key, i)}
                    className='flex h-11 items-center justify-start rounded-lg px-3.5 md:h-9 md:flex-1 md:justify-center md:px-1.5'
                  >
                    {label}
                  </Choice>
                ))}
                <Choice
                  on={v === 'na'}
                  disabled={!editable}
                  onPick={() => setScore(b.key, 'na')}
                  className='flex h-11 items-center justify-start rounded-lg px-3.5 md:ml-1.5 md:h-9 md:flex-1 md:justify-center md:px-1.5'
                >
                  Didn’t see this
                </Choice>
              </div>
              {err(mm.scores[k]) && <ErrorText>Pick one.</ErrorText>}
            </div>
          );
        })}
      </Card>

      <Card>
        <CardTitle>Top strength</CardTitle>
        <span className='-mt-2 text-[13px] leading-[150%] text-text-secondary'>Pick one.</span>
        <div role='radiogroup' aria-label='Top strength' className='flex flex-wrap gap-2'>
          {STRENGTHS.map((s) => (
            <Choice
              key={s.key}
              on={a.strength === s.key}
              disabled={!editable}
              onPick={() => set(() => ({ strength: s.key }))}
              className='h-11 rounded-full px-4 text-[14px] md:h-9'
            >
              {s.label}
            </Choice>
          ))}
        </div>
        {err(mm.strength) && <ErrorText>Pick one.</ErrorText>}
      </Card>

      <Card>
        <CardTitle>In your words</CardTitle>
        <Alert>
          Your name isn’t shown. In a small team they may still guess, so keep it respectful and
          based on real work.
        </Alert>
        <TextField
          label='What they did well'
          required
          value={a.praise ?? ''}
          readOnly={!editable}
          onChange={(v) => set(() => ({ praise: v }))}
          placeholder='e.g. Sent specs with every state, so I could build without asking.'
          error={err(mm.praise)}
        />
        <TextField
          label='What they could improve'
          required
          value={a.improve ?? ''}
          readOnly={!editable}
          onChange={(v) => set(() => ({ improve: v }))}
          placeholder='e.g. Share blockers in the thread, not only at check-ins.'
          error={err(mm.improve)}
        />
        <TextField
          label='Thanks'
          value={a.thanks ?? ''}
          readOnly={!editable}
          onChange={(v) => set(() => ({ thanks: v }))}
          placeholder='Thank them for something specific.'
        />
      </Card>

      <Card>
        <CardTitle>Would you work with them again?</CardTitle>
        <div
          role='radiogroup'
          aria-label='Would you work with them again?'
          className='grid grid-cols-2 gap-2'
        >
          {[
            ['yes', 'Yes'],
            ['no', 'No'],
          ].map(([v, label]) => (
            <Choice
              key={v}
              on={a.workAgain === v}
              disabled={!editable}
              onPick={() => set(() => ({ workAgain: v }))}
              className='h-11 rounded-lg text-[14px]'
            >
              {label}
            </Choice>
          ))}
        </div>
        <span className='text-[13px] leading-[150%] text-text-secondary'>
          Shown only as a total, never one by one.
        </span>
        {err(mm.workAgain) && <ErrorText>Pick one.</ErrorText>}
      </Card>

      <ChapterFooter
        editable={editable}
        savedText={savedText}
        saveError={saveError}
        errText={
          showErrors && n > 0
            ? `Answer the ${n} highlighted item${n > 1 ? 's' : ''} to continue.`
            : ''
        }
        onBackToHub={onBackToHub}
        actions={
          <>
            <TdButton variant='soft' size='l' onClick={onSaveBack}>
              Save and back
            </TdButton>
            <TdButton size='l' onClick={() => onNext(n)}>
              {nextLabel}
            </TdButton>
          </>
        }
      />
    </Page>
  );
};

export const RetroChapter = ({
  nav,
  retro,
  editable,
  savedText,
  saveError,
  onChange,
  onSaveBack,
  onNext,
  onBackToHub,
}) => (
  <Page>
    <Head
      nav={nav}
      kicker='Your chapter · optional'
      title='Retrospective'
      sub={editable ? 'Shown in your report as your own words.' : 'Read-only'}
    />
    <Card>
      <TextField
        label='What did you learn?'
        value={retro.learned}
        readOnly={!editable}
        onChange={(v) => onChange((r) => ({ ...r, learned: v }))}
        placeholder='e.g. Handoffs go faster when the receiver sees the checklist first.'
      />
      <TextField
        label='What’s your next goal?'
        value={retro.nextGoal}
        readOnly={!editable}
        onChange={(v) => onChange((r) => ({ ...r, nextGoal: v }))}
        placeholder='e.g. Lead a design review on my next project.'
      />
    </Card>
    <ChapterFooter
      editable={editable}
      savedText={savedText}
      saveError={saveError}
      onBackToHub={onBackToHub}
      actions={
        <>
          <TdButton variant='soft' size='l' onClick={onSaveBack}>
            Save and back
          </TdButton>
          <TdButton size='l' onClick={onNext}>
            Next: Kit items
          </TdButton>
        </>
      }
    />
  </Page>
);

export const KitChapter = ({
  nav,
  kit,
  candidates,
  editable,
  savedText,
  saveError,
  onChange,
  onKeep,
  onSkip,
  onBackToHub,
}) => {
  const picks = kit.picks ?? [];
  const isOn = (c) => picks.some((p) => samePick(p, c));
  const toggle = (c) =>
    onChange((k) => {
      const prev = k.picks ?? [];
      const on = prev.some((p) => samePick(p, c));
      return {
        picks: on ? prev.filter((p) => !samePick(p, c)) : [...prev, { type: c.type, id: c.id }],
        decided: false,
      };
    });
  const n = picks.length;
  return (
    <Page>
      <Head
        nav={nav}
        kicker='Your chapter · required'
        title='Kit items'
        sub={
          editable
            ? 'Your own check-in lines and handoff summaries. Pick what to keep, or keep none.'
            : 'Read-only · what you kept'
        }
      />
      <Alert>Only what you pick is saved.</Alert>
      <div className='flex flex-col overflow-hidden rounded-xl border border-green-deep03/[0.12] bg-grayscale-gray00'>
        {candidates.map((c, i) => {
          const on = isOn(c);
          return (
            <button
              key={`${c.type}-${c.id}`}
              type='button'
              role='checkbox'
              aria-checked={on}
              disabled={!editable}
              onClick={() => toggle(c)}
              className={cn(
                'flex min-h-16 select-none items-start gap-3 px-4 py-3.5 text-left hover:bg-green-deep03/[0.03] disabled:cursor-default md:min-h-[60px]',
                i > 0 && 'border-t border-green-deep03/[0.12]',
                on && 'bg-surface-green10 hover:bg-surface-green10',
              )}
            >
              {on ? (
                <span className='mt-px flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-green-deep02 text-grayscale-gray00'>
                  <TickIcon aria-hidden className='h-4 w-4' />
                </span>
              ) : (
                <span className='mt-px h-5 w-5 shrink-0 rounded-md bg-grayscale-gray00 shadow-[inset_0_0_0_1.5px_#b1ada8]' />
              )}
              <span className='flex min-w-0 flex-col gap-1'>
                <span className='text-[12px] font-medium leading-[140%] text-text-secondary'>
                  {c.tag}
                </span>
                <span className='text-[14px] leading-[150%] text-text-primary [text-wrap:pretty]'>
                  {c.text}
                </span>
              </span>
            </button>
          );
        })}
        {candidates.length === 0 && (
          <p className='px-4 py-4 text-[13px] leading-[150%] text-text-secondary'>
            You have no check-in lines or handoff summaries yet. Keep none to finish this chapter.
          </p>
        )}
      </div>
      {editable ? (
        <Footer>
          <span
            className={cn(
              'text-[13px] leading-[140%] tabular-nums',
              saveError ? 'text-text-failed' : 'text-text-secondary',
            )}
          >
            {saveError ? 'Couldn’t save. Check your connection.' : savedText}
          </span>
          <div className='flex w-full gap-2 md:w-auto [&>*]:flex-1 md:[&>*]:flex-none'>
            <TdButton variant='soft' size='l' onClick={onSkip}>
              Skip, keep none
            </TdButton>
            <TdButton size='l' disabled={n === 0} onClick={onKeep}>
              {n ? `Keep ${n} selected` : 'Keep selected'}
            </TdButton>
          </div>
        </Footer>
      ) : (
        <ChapterFooter editable={false} onBackToHub={onBackToHub} />
      )}
    </Page>
  );
};
